<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonMenuButton,
    IonButton,
    IonIcon,
    IonSpinner,
    IonRefresher,
    IonRefresherContent,
    IonModal,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonDatetime,
    IonToggle,
    IonFab,
    IonFabButton,
} from "@ionic/vue";
import type { RefresherCustomEvent } from "@ionic/vue";
import { refreshOutline, addOutline } from "ionicons/icons";
import type { Calendar, CalendarOptions, EventApi, EventInput, EventSourceFunc } from "@fullcalendar/core";

import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

import itLocale from "@fullcalendar/core/locales/it";
import { format } from "date-fns";
import { el, it as itDateLocale } from "date-fns/locale";
import { useCalendarStore } from "@/stores/calendar";
import type { CalendarEventDTO } from "@/types/calendar";

type CalendarEventExtendedProps = {
    description?: string | null;
    category?: string | null;
    location?: string | null;
    recurring?: boolean;
    recurrenceRule?: string | null;
    createdAt?: string | null;
    modifiedAt?: string | null;
};

const calendarStore = useCalendarStore();

const calendarComponent = ref<{ getApi: () => Calendar } | null>(null);
const activeEvent = ref<EventApi | null>(null);
const activeEventProps = computed<CalendarEventExtendedProps | null>(() => {
    if (!activeEvent.value) {
        return null;
    }
    return (activeEvent.value.extendedProps || null) as CalendarEventExtendedProps | null;
});
const errorMessage = ref<string | null>(null);
const pendingRefresher = ref<RefresherCustomEvent | null>(null);
const isEditing = ref(false);
const isCreating = ref(false);
const isSaving = ref(false);
const saveError = ref<string | null>(null);
const createEventModal = ref(false);
const eventModal = ref<HTMLIonModalElement | null>(null);
const datetimeModal = ref<{ isOpen: boolean; field: "start" | "end" | null; ready: boolean }>({
    isOpen: false,
    field: null,
    ready: false,
});
const editForm = reactive({
    title: "",
    start: "",
    end: "",
    allDay: false,
    location: "",
    description: "",
    category: "",
    color: "",
});

const isLoading = computed(() => calendarStore.isLoading);

const formatDateForApi = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm:ss");

const toEventInput = (event: CalendarEventDTO): EventInput => {
    const eventColor = event.color || undefined;
    const allDay = event.all_day ?? false;

    return {
        id: String(event.id),
        title: event.title,
        start: event.start,
        end: event.end ?? undefined,
        allDay,
        backgroundColor: eventColor,
        borderColor: eventColor,
        extendedProps: {
            description: event.description ?? null,
            category: event.category ?? null,
            location: event.location ?? null,
            recurring: event.recurring ?? false,
            recurrenceRule: event.recurrence_rule ?? null,
            createdAt: event.created_at ?? null,
            modifiedAt: event.modified_at ?? null,
        } as CalendarEventExtendedProps,
    };
};

const eventSource: EventSourceFunc = (fetchInfo, successCallback, failureCallback) => {
    errorMessage.value = null;
    activeEvent.value = null;
    calendarStore
        .fetchEvents({
            start: formatDateForApi(fetchInfo.start),
            end: formatDateForApi(fetchInfo.end),
        })
        .then((events) => {
            successCallback(events.map(toEventInput));
        })
        .catch((error) => {
            errorMessage.value = "Impossibile caricare gli eventi. Controlla la connessione e riprova.";
            failureCallback(error);
        });
};

const calendarOptions = ref<CalendarOptions>({
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: "dayGridMonth",
    headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,dayGridWeek,dayGridDay",
    },
    locales: [itLocale],
    locale: "it",
    nowIndicator: true,
    navLinks: true,
    height: "85vh",
    events: eventSource,
    eventClick: (info) => {
        if (info.view.type == "dayGridMonth") {
            info.jsEvent.preventDefault();
            calendarComponent.value?.getApi().changeView("dayGridDay", info.event.start!);
            return;
        }
        activeEvent.value = info.event;
    },
    dateClick: (info) => {
        if (info.view.type == "dayGridMonth") {
            console.log(info.dateStr);
            calendarComponent.value?.getApi().changeView("dayGridDay", info.dateStr);
        }
    },
});

const onModalDismiss = () => {
    activeEvent.value = null;
    isEditing.value = false;
    saveError.value = null;
};

const handleManualRefresh = () => {
    errorMessage.value = null;
    calendarComponent.value?.getApi().refetchEvents();
};

const handleRefresh = (event: RefresherCustomEvent) => {
    pendingRefresher.value = event;
    handleManualRefresh();
};

watch(isLoading, (loading) => {
    if (!loading && pendingRefresher.value) {
        pendingRefresher.value.detail.complete();
        pendingRefresher.value = null;
    }
});

const formatEventRange = (event: EventApi) => {
    const start = event.start;
    if (!start) {
        return "";
    }
    const end = event.end;
    const startLabel = format(start, event.allDay ? "PPP" : "PPPp", { locale: itDateLocale });
    if (!end) {
        return startLabel;
    }
    const effectiveEnd = event.allDay ? new Date(end.getTime() - 1) : end;
    const endLabel = format(effectiveEnd, event.allDay ? "PPP" : "PPPp", { locale: itDateLocale });
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
};

const normalizeDateInput = (value: string | null | undefined) => {
    return value ? formatDateForApi(new Date(value)) : "";
};

const formatDatetimeDisplay = (value: string | null | undefined) => {
    if (!value) return "";
    try {
        return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: itDateLocale });
    } catch {
        return value;
    }
};

const openDatetimeModal = (field: "start" | "end") => {
    datetimeModal.value = { isOpen: true, field, ready: false };
};

const onDatetimeModalPresent = () => {
    datetimeModal.value.ready = true;
};

const closeDatetimeModal = () => {
    datetimeModal.value = { isOpen: false, field: null, ready: false };
};

const handleDatetimeChange = (value: string | null | undefined) => {
    if (datetimeModal.value.field && value) {
        const normalized = normalizeDateInput(value);
        if (datetimeModal.value.field === "start") {
            editForm.start = normalized;
        } else {
            editForm.end = normalized;
        }
    }
    closeDatetimeModal();
};

const syncFormWithEvent = (event: EventApi | null) => {
    if (!event) {
        isEditing.value = false;
        return;
    }
    editForm.title = event.title || "";
    editForm.allDay = event.allDay || false;
    editForm.start = event.start ? formatDateForApi(event.start) : "";
    editForm.end = event.end ? formatDateForApi(event.end) : "";
    editForm.location = (event.extendedProps?.location as string) || "";
    editForm.description = (event.extendedProps?.description as string) || "";
    editForm.category = (event.extendedProps?.category as string) || "";
    editForm.color = (event.backgroundColor as string) || "";
};

watch(activeEvent, (event) => {
    syncFormWithEvent(event);
});

const toggleEdit = () => {
    isEditing.value = !isEditing.value;
    if (!isEditing.value) {
        syncFormWithEvent(activeEvent.value);
        saveError.value = null;
    } else {
        // @ts-ignore
        eventModal.value?.$el.setCurrentBreakpoint(1);
    }
};

const handleSave = async () => {
    if (!activeEvent.value) return;
    isSaving.value = true;
    saveError.value = null;
    try {
        const payload = {
            title: editForm.title.trim(),
            start: editForm.start || null,
            end: editForm.end || null,
            all_day: editForm.allDay,
            description: editForm.description?.trim() || null,
            location: editForm.location?.trim() || null,
            category: editForm.category?.trim() || null,
            recurring: activeEventProps.value?.recurring ?? false,
            recurrence_rule: activeEventProps.value?.recurrenceRule ?? null,
            color: editForm.color?.trim() || null,
        };
        await calendarStore.updateEvent(Number(activeEvent.value.id), payload);
        await calendarComponent.value?.getApi().refetchEvents();
        isEditing.value = false;
        activeEvent.value = null;
    } catch (error) {
        saveError.value = "Impossibile salvare le modifiche. Riprova.";
    } finally {
        isSaving.value = false;
    }
};

const resetEditForm = () => {
    editForm.title = "";
    editForm.start = formatDateForApi(new Date());
    editForm.end = "";
    editForm.allDay = false;
    editForm.location = "";
    editForm.description = "";
    editForm.category = "";
    editForm.color = "#3880ff";
};

const openCreateModal = () => {
    resetEditForm();
    isCreating.value = true;
    createEventModal.value = true;
};

const closeCreateModal = () => {
    createEventModal.value = false;
    isCreating.value = false;
    saveError.value = null;
};

const handleCreate = async () => {
    isSaving.value = true;
    saveError.value = null;
    try {
        const payload = {
            title: editForm.title.trim() || "Nuovo evento",
            start: editForm.start || formatDateForApi(new Date()),
            end: editForm.end || null,
            all_day: editForm.allDay,
            description: editForm.description?.trim() || null,
            location: editForm.location?.trim() || null,
            category: editForm.category?.trim() || null,
            recurring: false,
            recurrence_rule: null,
            color: editForm.color?.trim() || null,
        };
        await calendarStore.createEvent(payload);
        await calendarComponent.value?.getApi().refetchEvents();
        closeCreateModal();
    } catch (error) {
        saveError.value = "Impossibile creare l'evento. Riprova.";
        console.error(error);
    } finally {
        isSaving.value = false;
    }
};
</script>

<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-menu-button></ion-menu-button>
                </ion-buttons>
                <ion-title>Calendario</ion-title>
                <ion-buttons slot="end">
                    <ion-button fill="clear" @click="handleManualRefresh" :disabled="isLoading">
                        <ion-icon slot="icon-only" :icon="refreshOutline" />
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-refresher slot="fixed" @ionRefresh="handleRefresh">
                <ion-refresher-content />
            </ion-refresher>

            <div class="calendar-wrapper">
                <FullCalendar ref="calendarComponent" :options="calendarOptions" />
                <div v-if="isLoading" class="calendar-spinner">
                    <ion-spinner name="crescent" />
                </div>
            </div>

            <div v-if="errorMessage" class="calendar-error">
                {{ errorMessage }}
            </div>

            <ion-modal
                ref="eventModal"
                :is-open="!!activeEvent"
                :initial-breakpoint="0.5"
                :breakpoints="[0, 0.25, 0.5, 1]"
                @didDismiss="onModalDismiss">
                <div class="event-details">
                    <div class="event-details__header" v-if="activeEvent">
                        <ion-button size="small" fill="clear" color="medium" @click="activeEvent = null">
                            Chiudi
                        </ion-button>
                        <div class="event-details__actions">
                            <ion-button
                                size="small"
                                fill="outline"
                                color="medium"
                                @click="toggleEdit"
                                :disabled="isSaving">
                                {{ isEditing ? "Annulla" : "Modifica" }}
                            </ion-button>
                            <ion-button
                                size="small"
                                v-if="isEditing"
                                color="primary"
                                @click="handleSave"
                                :disabled="isSaving">
                                <ion-spinner v-if="isSaving" slot="start" name="crescent" />
                                Salva
                            </ion-button>
                        </div>
                    </div>
                    <div style="overflow-y: auto">
                        <template v-if="activeEvent && !isEditing">
                            <h2 class="event-details__title" v-if="activeEvent">{{ activeEvent.title }}</h2>
                            <p class="event-details__range" v-if="activeEvent">{{ formatEventRange(activeEvent) }}</p>
                            <p v-if="activeEvent?.allDay && !isEditing" class="event-details__line">
                                Evento per l'intera giornata
                            </p>
                            <p v-if="activeEventProps?.location && !isEditing" class="event-details__line">
                                Luogo: {{ activeEventProps.location }}
                            </p>
                            <p v-if="activeEventProps?.category && !isEditing" class="event-details__line">
                                Categoria: {{ activeEventProps.category }}
                            </p>
                            <p v-if="activeEventProps?.recurring && !isEditing" class="event-details__line">
                                Ricorrente
                                <span v-if="activeEventProps?.recurrenceRule">
                                    ({{ activeEventProps.recurrenceRule }})
                                </span>
                            </p>
                            <p v-if="activeEventProps?.description && !isEditing" class="event-details__description">
                                {{ activeEventProps.description }}
                            </p>
                        </template>
                        <ion-list
                            style="
                                height: 87vh;
                                overflow-y: auto;
                                background: transparent;
                                --background: var(--ion-background-color);
                            "
                            v-if="isEditing"
                            class="event-details__form">
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Titolo</ion-label>
                                <ion-input
                                    :value="editForm.title"
                                    @ionInput="(ev) => (editForm.title = (ev.detail.value as string) || '')"
                                    placeholder="Titolo evento" />
                            </ion-item>

                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label>Giornata intera</ion-label>
                                <ion-toggle
                                    :checked="editForm.allDay"
                                    slot="end"
                                    @ionChange="(ev) => (editForm.allDay = !!ev.detail.checked)" />
                            </ion-item>
                            <ion-item
                                lines="full"
                                v-if="!editForm.allDay"
                                button
                                @click="openDatetimeModal('start')"
                                style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Inizio</ion-label>
                                <ion-input
                                    readonly
                                    :value="formatDatetimeDisplay(editForm.start)"
                                    placeholder="Seleziona data e ora" />
                            </ion-item>
                            <ion-item
                                lines="full"
                                v-if="!editForm.allDay"
                                button
                                @click="openDatetimeModal('end')"
                                style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Fine</ion-label>
                                <ion-input
                                    readonly
                                    :value="formatDatetimeDisplay(editForm.end)"
                                    placeholder="Seleziona data e ora" />
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Categoria</ion-label>
                                <ion-input
                                    :value="editForm.category"
                                    @ionInput="(ev) => (editForm.category = (ev.detail.value as string) || '')"
                                    placeholder="Categoria" />
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Colore</ion-label>
                                <div class="color-picker-wrapper">
                                    <input
                                        type="color"
                                        :value="editForm.color || '#3880ff'"
                                        @input="(ev) => (editForm.color = (ev.target as HTMLInputElement).value)"
                                        class="color-picker-input" />
                                    <span class="color-picker-value">{{ editForm.color || "#3880ff" }}</span>
                                </div>
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Luogo</ion-label>
                                <ion-input
                                    :value="editForm.location"
                                    @ionInput="(ev) => (editForm.location = (ev.detail.value as string) || '')"
                                    placeholder="Inserisci luogo" />
                            </ion-item>
                            <ion-item lines="none" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Descrizione</ion-label>
                                <ion-textarea
                                    auto-grow
                                    :value="editForm.description"
                                    @ionInput="(ev) => (editForm.description = (ev.detail.value as string) || '')"
                                    placeholder="Aggiungi dettagli" />
                            </ion-item>
                        </ion-list>
                        <p v-if="saveError" class="event-details__error">{{ saveError }}</p>
                    </div>
                </div>
            </ion-modal>

            <!-- Datetime Picker Modal -->
            <ion-modal
                :is-open="datetimeModal.isOpen"
                @didPresent="onDatetimeModalPresent"
                @didDismiss="closeDatetimeModal"
                :initial-breakpoint="1"
                :breakpoints="[1]">
                <div class="datetime-modal">
                    <div class="datetime-modal__header">
                        <ion-button fill="clear" @click="closeDatetimeModal">Annulla</ion-button>
                        <span class="datetime-modal__title">
                            {{ datetimeModal.field === "start" ? "Inizio" : "Fine" }}
                        </span>
                        <ion-button
                            fill="clear"
                            @click="
                                handleDatetimeChange(datetimeModal.field === 'start' ? editForm.start : editForm.end)
                            ">
                            Conferma
                        </ion-button>
                    </div>
                    <ion-datetime
                        v-if="datetimeModal.ready"
                        presentation="date-time"
                        hour-cycle="h23"
                        class="datetime-picker"
                        :value="datetimeModal.field === 'start' ? editForm.start : editForm.end"
                        @ionChange="
                            (ev) => {
                                if (datetimeModal.field === 'start') {
                                    editForm.start = normalizeDateInput(ev.detail.value);
                                } else {
                                    editForm.end = normalizeDateInput(ev.detail.value);
                                }
                            }
                        " />
                </div>
            </ion-modal>

            <!-- Create Event Modal -->
            <ion-modal
                :is-open="createEventModal"
                :initial-breakpoint="1"
                :breakpoints="[0, 1]"
                @didDismiss="closeCreateModal">
                <div class="event-details">
                    <div class="event-details__header">
                        <ion-button size="small" fill="clear" color="medium" @click="closeCreateModal">
                            Annulla
                        </ion-button>
                        <span class="event-details__modal-title">Nuovo Evento</span>
                        <ion-button size="small" color="primary" @click="handleCreate" :disabled="isSaving">
                            <ion-spinner v-if="isSaving" slot="start" name="crescent" />
                            Crea
                        </ion-button>
                    </div>
                    <div style="overflow-y: auto">
                        <ion-list
                            style="
                                height: 87vh;
                                overflow-y: auto;
                                background: transparent;
                                --background: var(--ion-background-color);
                            "
                            class="event-details__form">
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Titolo</ion-label>
                                <ion-input
                                    :value="editForm.title"
                                    @ionInput="(ev) => (editForm.title = (ev.detail.value as string) || '')"
                                    placeholder="Titolo evento" />
                            </ion-item>

                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label>Giornata intera</ion-label>
                                <ion-toggle
                                    :checked="editForm.allDay"
                                    slot="end"
                                    @ionChange="(ev) => (editForm.allDay = !!ev.detail.checked)" />
                            </ion-item>
                            <ion-item
                                lines="full"
                                v-if="!editForm.allDay"
                                button
                                @click="openDatetimeModal('start')"
                                style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Inizio</ion-label>
                                <ion-input
                                    readonly
                                    :value="formatDatetimeDisplay(editForm.start)"
                                    placeholder="Seleziona data e ora" />
                            </ion-item>
                            <ion-item
                                lines="full"
                                v-if="!editForm.allDay"
                                button
                                @click="openDatetimeModal('end')"
                                style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Fine</ion-label>
                                <ion-input
                                    readonly
                                    :value="formatDatetimeDisplay(editForm.end)"
                                    placeholder="Seleziona data e ora" />
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Categoria</ion-label>
                                <ion-input
                                    :value="editForm.category"
                                    @ionInput="(ev) => (editForm.category = (ev.detail.value as string) || '')"
                                    placeholder="Categoria" />
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Colore</ion-label>
                                <div class="color-picker-wrapper">
                                    <input
                                        type="color"
                                        :value="editForm.color || '#3880ff'"
                                        @input="(ev) => (editForm.color = (ev.target as HTMLInputElement).value)"
                                        class="color-picker-input" />
                                    <span class="color-picker-value">{{ editForm.color || "#3880ff" }}</span>
                                </div>
                            </ion-item>
                            <ion-item lines="full" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Luogo</ion-label>
                                <ion-input
                                    :value="editForm.location"
                                    @ionInput="(ev) => (editForm.location = (ev.detail.value as string) || '')"
                                    placeholder="Inserisci luogo" />
                            </ion-item>
                            <ion-item lines="none" style="--background: var(--ion-background-color)">
                                <ion-label position="stacked">Descrizione</ion-label>
                                <ion-textarea
                                    auto-grow
                                    :value="editForm.description"
                                    @ionInput="(ev) => (editForm.description = (ev.detail.value as string) || '')"
                                    placeholder="Aggiungi dettagli" />
                            </ion-item>
                        </ion-list>
                        <p v-if="saveError" class="event-details__error">{{ saveError }}</p>
                    </div>
                </div>
            </ion-modal>

            <!-- FAB Button -->
            <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                <ion-fab-button @click="openCreateModal">
                    <ion-icon :icon="addOutline" />
                </ion-fab-button>
            </ion-fab>
        </ion-content>
    </ion-page>
</template>

<style scoped>
.calendar-wrapper {
    position: relative;
    padding: 0;
    background: var(--ion-card-background, #fff);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    margin: 8px;
    overflow-x: auto;
}

.calendar-spinner {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--ion-color-light-rgb), 0.8);
    backdrop-filter: blur(4px);
    z-index: 10;
}

.calendar-error {
    margin: 16px;
    padding: 12px;
    border-radius: 8px;
    background: var(--ion-color-danger-tint);
    color: var(--ion-color-danger-contrast);
    text-align: center;
    font-weight: 500;
}

.event-details {
    padding: 24px;
    height: 100%;
    background: var(--ion-background-color, #fff);
}

.event-details__title {
    margin: 0 0 12px;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ion-text-color);
}

.event-details__range {
    margin: 0 0 16px;
    font-size: 0.95rem;
    color: var(--ion-color-medium);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
}

.event-details__line {
    margin: 8px 0;
    font-size: 0.95rem;
    color: var(--ion-text-color);
    display: flex;
    align-items: center;
    gap: 8px;
}

.event-details__description {
    margin: 16px 0 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--ion-text-color);
    white-space: pre-wrap;
    padding-top: 16px;
    border-top: 1px solid var(--ion-color-light-shade);
}

.event-details__header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.event-details__modal-title {
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--ion-text-color);
}

.event-details__actions {
    display: flex;
    gap: 8px;
}

.event-details__form {
    margin-top: 12px;
    overflow: hidden;
}

.event-details__error {
    margin-top: 12px;
    color: var(--ion-color-danger);
    font-weight: 600;
}

:deep(.fc-button-group) {
    gap: 4px;
}

/* FullCalendar Customization */
:deep(.fc) {
    --fc-border-color: var(--ion-color-light-shade);
    --fc-page-bg-color: transparent;
    --fc-neutral-bg-color: var(--ion-color-light);
    --fc-list-event-hover-bg-color: var(--ion-color-light-shade);
    --fc-today-bg-color: rgba(var(--ion-color-primary-rgb), 0.05);

    font-family: inherit;
    padding: 8px;
}

/* Toolbar */
:deep(.fc-header-toolbar) {
    margin-bottom: 24px !important;
    flex-wrap: wrap;
    gap: 12px;
}

:deep(.fc-toolbar-title) {
    font-size: 1.2rem !important;
    font-weight: 700;
    color: var(--ion-text-color);
    text-transform: capitalize;
}

/* Buttons */
:deep(.fc-button) {
    border-radius: 12px !important;
    text-transform: capitalize;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 8px 16px !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
}

:deep(.fc-button-primary) {
    background-color: var(--ion-color-light) !important;
    border-color: transparent !important;
    color: var(--ion-text-color) !important;
}

:deep(.fc-button-primary:hover) {
    background-color: var(--ion-color-light-shade) !important;
}

:deep(.fc-button-primary:not(:disabled).fc-button-active),
:deep(.fc-button-primary:not(:disabled):active) {
    background-color: var(--ion-color-primary) !important;
    color: var(--ion-color-primary-contrast) !important;
}

:deep(.fc-button-primary:disabled) {
    opacity: 0.5;
}

/* Grid */
:deep(.fc-theme-standard th) {
    border: none;
    padding-bottom: 8px;
}

:deep(.fc-col-header-cell-cushion) {
    color: var(--ion-color-medium);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    text-decoration: none !important;
}

:deep(.fc-scrollgrid) {
    border: none !important;
}

:deep(.fc-daygrid-day-frame) {
    min-height: 80px;
}

:deep(.fc-daygrid-day-top) {
    flex-direction: row;
    padding: 8px;
}

:deep(.fc-daygrid-day-number) {
    color: var(--ion-text-color);
    font-weight: 500;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    text-decoration: none !important;
    font-size: 0.9rem;
}

:deep(.fc-day-today .fc-daygrid-day-number) {
    background-color: var(--ion-color-primary);
    color: var(--ion-color-primary-contrast);
}

/* Events */
:deep(.fc-event) {
    border: none !important;
    border-radius: 6px;
    padding: 2px 4px;
    font-size: 0.85rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: transform 0.1s ease;
}

:deep(.fc-event:hover) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

:deep(.fc-daygrid-event-dot) {
    border-color: currentColor;
}

/* Week View Specifics */
:deep(.fc-dayGridWeek-view .fc-scrollgrid) {
    min-width: 1400px;
    /* border: 1px solid var(--ion-color-medium-tint) !important; */
}

:deep(.fc-dayGridWeek-view .fc-col-header .fc-col-header-cell) {
    border-bottom: 1px solid var(--ion-color-medium-tint) !important;
}

:deep(.fc-dayGridWeek-view td.fc-day) {
    border: 1px solid var(--ion-color-medium-tint) !important;
}

:deep(.fc-dayGridMonth-view .fc-daygrid-day-top) {
    padding: 0;
}

:deep(.fc-dayGridMonth-view .fc-daygrid-day-events) {
    border-right: 1px solid var(--ion-color-medium-tint) !important;
    min-height: 3.5em;
    overflow: hidden;
}

:deep(.fc-dayGridMonth-view .fc-daygrid-event) {
    padding: 0;
}

/* Only on large screens */
@media (min-width: 768px) {
    :deep(.fc-dayGridMonth-view .fc-daygrid-event) {
        padding: 2px 4px !important;
    }
}

:deep(.fc-dayGridMonth-view .fc-day:last-child .fc-daygrid-day-events) {
    border-right: none !important;
}

:deep(.fc-dayGridWeek-view) {
    width: 100%;
    overflow-x: auto;
}

.datetime-size-fixed {
    max-width: unset !important;
}

.datetime-modal {
    padding: 16px;
    height: 100%;
    background: var(--ion-background-color);
    display: flex;
    flex-direction: column;
}

.datetime-modal__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.datetime-modal__title {
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--ion-text-color);
}

.datetime-modal ion-datetime {
    margin: 0 auto;
}

/* Ensure datetime picker text is visible */
:deep(ion-datetime) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .datetime-calendar-picker) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .calendar-day) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .picker-item) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .picker-opt) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .time-body) {
    color: var(--ion-text-color);
}

:deep(ion-datetime .datetime-time .time-body) {
    color: var(--ion-text-color);
}

:deep(ion-datetime::part(wheel-item)) {
    color: var(--ion-text-color);
}

:deep(ion-datetime::part(time-button)) {
    color: var(--ion-text-color);
}

.color-picker-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    width: 100%;
}

.color-picker-input {
    width: 48px;
    height: 48px;
    padding: 0;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: transparent;
}

.color-picker-input::-webkit-color-swatch-wrapper {
    padding: 0;
}

.color-picker-input::-webkit-color-swatch {
    border: 2px solid var(--ion-color-light-shade);
    border-radius: 8px;
}

.color-picker-input::-moz-color-swatch {
    border: 2px solid var(--ion-color-light-shade);
    border-radius: 8px;
}

.color-picker-value {
    font-family: monospace;
    font-size: 0.95rem;
    color: var(--ion-color-medium);
}
</style>

<style>
/* Global styles for ion-datetime - must be unscoped to work with shadow DOM */
.datetime-picker {
    --background: var(--ion-background-color);
    --background-rgb: var(--ion-background-color-rgb);
    --title-color: var(--ion-text-color);
    color: var(--ion-text-color);
}

.datetime-picker .calendar-month-year {
    color: var(--ion-text-color) !important;
}

.datetime-picker .calendar-day-number {
    color: var(--ion-text-color) !important;
}

.datetime-picker .datetime-calendar {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker {
    --wheel-highlight-background: rgba(var(--ion-color-primary-rgb), 0.1);
    --wheel-fade-background-rgb: var(--ion-background-color-rgb, 255, 255, 255);
}

ion-datetime.datetime-picker::part(wheel-item) {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker::part(wheel-item active) {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker::part(time-button) {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker::part(month-year-button) {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker::part(calendar-day) {
    color: var(--ion-text-color) !important;
}

ion-datetime.datetime-picker::part(calendar-day active) {
    color: var(--ion-color-primary-contrast) !important;
}
</style>
